import { debounce } from '@hh/helper-functions'

class ShadeFinder extends HHBaseElement {
  constructor() {
    super()
    this.isReady = false
    this._isOpen = false

    this.$slidePanel = this.closest('slide-panel')
    this.$contentWrapper = this.querySelector('.shade-finder__contents-wrapper')

    this.$simpleslider = this.querySelector('simple-slider')
    this.$continueButton = this.querySelector('[data-slider-next]')
    this.mql = window.matchMedia('(min-width: 992px)')

    this._openMobileCamera = this._openMobileCamera.bind(this)
    this._handleOpened = this._handleOpened.bind(this)
    this._handleClosed = this._handleClosed.bind(this)
    this._handleAddToBag = this._handleAddToBag.bind(this)
    this._handleInslidePanelOpen = this._handleInslidePanelOpen.bind(this)
    this._handleInslidePanelClose = this._handleInslidePanelClose.bind(this)
    this._recommendationMapping = this._recommendationMapping.bind(this)
    this._fetchProductsBySkus = this._fetchProductsBySkus.bind(this)
    this._variantsMapping = this._variantsMapping.bind(this)
    this._drawRecommendations = this._drawRecommendations.bind(this)

    this.skuList = []
    this.recommendations = {}
    this.variants = {}
    this._initScript()
  }

  get isOpen() {
    return this._isOpen
  }

  set isOpen(value) {
    this._isOpen = value
  }

  get stepCounts() {
    return Number(this.dataset.steps) || 0
  }

  get apiKey() {
    return this.getAttribute('api-key')
  }

  get widgetId() {
    return this.getAttribute('widget-id')
  }

  get language() {
    return this.getAttribute('language')
  }

  get privacyPolicyURL() {
    return this.getAttribute('privacy-policy')
  }

  get accessToken() {
    return this.getAttribute('access-token') || ''
  }

  get title() {
    return this.getAttribute('title') || ''
  }

  get screenReaderText() {
    return this.getAttribute('screen-reader-text') || ''
  }

  get shadowStylesheetSwiperURL() {
    return this.getAttribute('shadowStylesheetSwiper') || ''
  }

  connectedCallback() {
    this.$continueButton.addEventListener('click', this._openMobileCamera)

    this.$slidePanel.addEventListener(
      'slide-panel-open',
      this._handleInslidePanelOpen
    )
    this.$slidePanel.addEventListener(
      'slide-panel-close',
      this._handleInslidePanelClose
    )
  }

  disconnectedCallback() {
    this.$continueButton.removeEventListener('click', this._openMobileCamera)

    this.$slidePanel.removeEventListener(
      'slide-panel-open',
      this._handleInslidePanelOpen
    )
    this.$slidePanel.removeEventListener(
      'slide-panel-close',
      this._handleInslidePanelClose
    )

    this._resetWidget()
  }

  _initScript() {
    if (document.getElementById('ymk-shade-finder-script')) {
      if (!this.hasAttribute('no-init')) {
        this.show()
      }
    } else {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.id = 'ymk-shade-finder-script'
      window.ymkAsyncInit = () => {
        this._initWidget()
      }
      script.src = `https://plugins-media.perfectcorp.com/${this.widgetId}/sdk.js?apiKey=${this.apiKey}`
      document.body.appendChild(script)
    }
  }

  _initWidget() {
    this.isReady = true
    YMK.addEventListener('loaded', this._handleOpened)
    YMK.addEventListener('opened', this._handleOpened)
    YMK.addEventListener('closed', this._handleClosed)
    YMK.addEventListener('recommendationMapping', this._recommendationMapping)

    const options = {
      autoOpen: false,
      language: this.language,
      toolPanelBottomOffsetY: 370,
      privacyPolicyURL: this.privacyPolicyURL
    }
    YMK.init(options)

    if (!this.hasAttribute('no-init') && this.mql.matches) {
      this.show()
    }
  }

  _handleOpened(e) {
    this.isOpen = true
  }

  _resetWidget() {
    YMK.removeEventListener('loaded', this._handleOpened)
    YMK.removeEventListener('opened', this._handleOpened)
    YMK.removeEventListener('closed', this._handleClosed)
    YMK.removeEventListener(
      'recommendationMapping',
      this._recommendationMapping
    )

    this._initWidget()
  }

  _handleClosed() {
    this.$contentWrapper.classList.remove('shade-finder__rendered')
    this.$slidePanel.classList.remove('mobile-opened')
    this.isOpen = false

    if (this.$slidePanel.open) {
      setTimeout(() => {
        this.$slidePanel._close()
      }, 500)
    }
  }

  uniqArray(arr) {
    return arr.filter((v, i, self) => i === self.indexOf(v))
  }

  async _recommendationMapping(json) {
    // Unique Recommendations based of the productId property
    const recommendationsMap = json.recommendations.reduce((acc, item) => {
      const productId = item.productId
      if (!acc[productId]) {
        acc[productId] = item
      }
      return acc
    }, {})

    const recommendations = Object.values(recommendationsMap)
    this.$contentWrapper.classList.add('shade-finder__loading')

    this.matchedSkuList = {}
    this.coolerSkuList = {}
    this.warmerSkuList = {}
    this.lighterSkuList = {}
    this.deeperSkuList = {}
    this.variants = {}
    this.skuList = recommendations.reduce((skus, item) => {
      if (typeof item === 'object') {
        if (typeof item['matched']['skuId'] !== 'undefined') {
          skus.push(item['matched']['skuId'])
          this.matchedSkuList[item['matched']['skuId']] =
            item['matched']['guid']
        }
        if (typeof item['cooler']['skuId'] !== 'undefined') {
          skus.push(item['cooler']['skuId'])
          this.coolerSkuList[item['cooler']['skuId']] = item['cooler']['guid']
        }
        if (typeof item['warmer']['skuId'] !== 'undefined') {
          skus.push(item['warmer']['skuId'])
          this.warmerSkuList[item['warmer']['skuId']] = item['warmer']['guid']
        }
        if (typeof item['lighter']['skuId'] !== 'undefined') {
          skus.push(item['lighter']['skuId'])
          this.lighterSkuList[item['lighter']['skuId']] =
            item['lighter']['guid']
        }
        if (typeof item['darker']['skuId'] !== 'undefined') {
          skus.push(item['darker']['skuId'])
          this.deeperSkuList[item['darker']['skuId']] = item['darker']['guid']
        }
      }
      return skus
    }, [])
    this.skuList = this.uniqArray(this.skuList)
    await this._fetchProductsBySkus(this.skuList[0])

    this._drawRecommendations()
  }

  async _fetchProductsBySkus(sku) {
    const query = `query recommendedProducts ($filter: String!) {
      products(first: 1, query: $filter) {
        edges {
          node {
            title
            id
            handle
            variants(first: 50) {
              edges {
                node {
                  id
                  availableForSale
                  price{
                    amount
                    currencyCode
                  }
                  image {
                    src
                    altText
                  }
                  sku
                  title
                  hex: metafield(namespace: "swatch",key: "hex_code") {
                    value
                  }
                }
              }
            }
          }
        }
      }
    }`
    const params = {
      query,
      variables: { filter: `${sku}` },
    }

    const response = await this.fetchQuery(params)
    const newSearchSKU = this._variantsMapping(response, sku)
    if (newSearchSKU) {
      debounce(await this._fetchProductsBySkus(newSearchSKU), 350)
    }
  }

  _variantsMapping(response, sku) {
    const product = response?.data?.products?.edges[0]?.node
    if (product) {
      product.variants.edges.forEach((variant) => {
        if (this.skuList.includes(variant.node.sku)) {
          this.variants[String(variant.node.sku)] = variant.node
          this.variants[String(variant.node.sku)].handle = product.handle
          this.variants[String(variant.node.sku)].productTitle = product.title
        }
      })
    } else if (sku) {
      // Remove sku from list if product not found
      this.skuList = this.skuList.filter((_sku) => _sku !== sku)
    }

    const keysOfVariants = Object.keys(this.variants)
    let newSearchSKU = ''
    for (let sku of this.skuList) {
      if (!keysOfVariants.includes(sku)) {
        newSearchSKU = sku
        break
      }
    }
    return newSearchSKU
  }

  show() {
    if (!this.isOpen) {
      setTimeout(() => {
        YMK.openShadeFinder(true)
      }, 500)
    }
  }

  _handleInslidePanelOpen() {
    this.show()
  }

  _handleInslidePanelClose() {
    YMK.closeEngine()
    this.$contentWrapper.classList.remove('shade-finder__rendered')
    this.$slidePanel.classList.remove('mobile-opened')
  }

  _openMobileCamera(event) {
    const activeIndex = this.$simpleslider.activeIndex + 1
    if (this.stepCounts === activeIndex) {
      this.$slidePanel.classList.add('mobile-opened')
    }
  }

  _drawRecommendations() {
    this.$matchedContainer = this.querySelector(
      '.shade-finder__tab-content[data-handle="recommended"]'
    )
    this.$coolerContainer = this.querySelector(
      '.shade-finder__tab-content[data-handle="cooler"]'
    )
    this.$warmerContainer = this.querySelector(
      '.shade-finder__tab-content[data-handle="warmer"]'
    )
    this.$lighterContainer = this.querySelector(
      '.shade-finder__tab-content[data-handle="lighter"]'
    )
    this.$deeperContainer = this.querySelector(
      '.shade-finder__tab-content[data-handle="deeper"]'
    )

    this._drawRecommendationsSlider(
      this.$matchedContainer,
      this.matchedSkuList,
      true
    )
    this._drawRecommendationsSlider(this.$coolerContainer, this.coolerSkuList)
    this._drawRecommendationsSlider(this.$warmerContainer, this.warmerSkuList)
    this._drawRecommendationsSlider(this.$lighterContainer, this.lighterSkuList)
    this._drawRecommendationsSlider(this.$deeperContainer, this.deeperSkuList)

    this.$contentWrapper.classList.add('shade-finder__rendered')
    this.$contentWrapper.classList.remove('shade-finder__loading')
  }

  _decodeProductVariantID(id) {
    return id.split('gid://shopify/ProductVariant/')[1]
  }

  formatPrice(price) {
    return Shopify.formatMoney(price * 100, window.money_format)
  }

  _drawRecommendationsSlider(container, skuList, firstTab = false) {
    container.innerHTML = ''
    if (Object.keys(skuList).length > 0) {
      const heading = container.dataset.heading || ''
      let html = `<swiper-container aria-label="${this.title} Carousel" init="false" navigation="true" role="region"><p class="visually-hidden">${this.screenReaderText}</p>`
      let variant = null
      Object.keys(skuList).forEach((sku) => {
        variant = this.variants[sku]
        if (variant && variant.availableForSale) {
          html += `
            <swiper-slide class="swiper-slide" data-guid="${
              skuList[sku]
            }" data-ymk-dataLayer='{"event": "vto_matched_try_on","item_name": "${
            variant.productTitle
          }","shade_name": "${variant.title}","item_price": "${
            variant.price.amount
          }","currency": "${variant.price.currencyCode}"}'>
              <div class="shade-finder__product">
                <div class="shade-finder__product-container flex flex-direction-column">
                  <div class="flex shade-finder__product-image">
                    <img src="${this.imgURL(variant.image.src, '300x')}" alt=" " width="300" height="300" loading="lazy" />
                  </div>
                  <div class="shade-finder__product-info flex flex-direction-column">
                    <h4 class="no-margin uppercase">${heading}</h4>
                    <p class="no-margin b3">${variant.productTitle}</p>
                    <div class="shade-finder__variant">
                      <div class="shade-finder__variant-swatch" style="background: ${
                        variant.hex?.value
                      };"></div>
                      <span class="f2">${variant.title}</span>
                    </div>
                  </div>
                  <div class="shade-finder__product-action">
                    <button class="button js-shade-add-to-bag" type="button" tabindex="0" data-variant-id="${this._decodeProductVariantID(
                      variant.id
                    )}">
                      <span>${
                        window.variantStrings.addToCart
                      } • ${this.formatPrice(variant.price.amount)}</span>
                      <div class="lds-ring">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </swiper-slide>
          `
        }
      })
      html += '</<swiper-container>'
      container.innerHTML = html

      const swiperEl = container.querySelector('swiper-container')
      const swiperParams = {
        slidesPerView: 1,
        injectStylesUrls: [this.shadowStylesheetSwiperURL],
        slideToClickedSlide: true,
        breakpoints: {
          640: {
            slidesPerView: 2,
          },
          768: {
            slidesPerView: 1,
          },
          992: {
            slidesPerView: 2,
          },
          1200: {
            slidesPerView: 3,
          },
          1330: {
            slidesPerView: 4,
          },
        },
        on: {
          afterInit: (swiper) => {
            if (firstTab) {
              setTimeout(() => {
                this._handleSlideSelectOrClick(swiper, 0)
              }, 150)
            }
          },
          slideChange: (swiper) => {
            this._handleSlideSelectOrClick(swiper)
          },
          click: (swiper) => {
            this.wasClicked = true
            this._handleSlideSelectOrClick(swiper)
            this.wasClicked = false
          },
        },
      }
      // now we need to assign all parameters to Swiper element
      Object.assign(swiperEl, swiperParams)
      // and now initialize it
      swiperEl.initialize()

      // Add event - Add to Bag
      container.querySelectorAll('.js-shade-add-to-bag').forEach((button) => {
        button.addEventListener('click', this._handleAddToBag.bind(this))
      })
    }
  }

  _handleSlideSelectOrClick(swiper, targetIndex = null) {
    swiper.slides.forEach((slide) => {
      slide.classList.remove('selected')
    })
    const showIndex =
      typeof targetIndex === 'number' ? targetIndex : swiper.activeIndex
    const clickIndex =
      targetIndex === null && this.wasClicked ? swiper.clickedIndex : null
    const slide =
      swiper.slides[clickIndex ?? showIndex ?? swiper.activeIndex] ??
      swiper.slides[0]
    if (!slide) return
    const guid = slide.dataset.guid
    const ymkDatalayer = slide.dataset.ymkDatalayer
    YMK.applyMakeupBySku(guid)
    dataLayer.push(JSON.parse(ymkDatalayer))
    slide.classList.add('selected')
  }

  async _handleAddToBag(e) {
    e.preventDefault()
    const triggerButton = e.currentTarget
    const variantId = triggerButton.dataset.variantId || ''
    triggerButton.setAttribute('loading', true)
    if (variantId) {
      await window.__CART__CONTROLLER__.add([
        {
          id: variantId,
          quantity: 1,
        }
      ], false,
      true)
      triggerButton.removeAttribute('loading')
    }
  }

  imgURL(src, size) {
    // remove any current image size then add the new image size
    return src
      .replace(
        /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g,
        '.'
      )
      .replace(/\.jpg|\.png|\.gif|\.jpeg/g, function (match) {
        return '_' + size + match
      })
  }

  async fetchQuery(params) {
    const optionsQuery = {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Shopify-Storefront-Access-Token': this.accessToken,
      },
      body: JSON.stringify(params),
    }

    const response = await fetch(
      window.routes.shop_url + `/api/graphql`,
      optionsQuery
    )
    return await response.json()
  }
}

window.customElements.define('shade-finder', ShadeFinder)
